// Lib
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

// Graph
import { organizationCampaignAuthTaskList } from '@graphql/queries/campaign';

// Api
import client from '@api';
import { sharedQuery, sharedQueryKey } from '@utils/sharedQuery';

// Types
import type { RootState } from '@app-types/index';

/* Whether a BRAND org participates (as the creator side) in anyone else's
 * campaign — drives the "Invited" campaigns tab and the Offers nav entry, so a
 * brand without invitations sees no extra UI. Creator orgs always have that UI,
 * so the probe is skipped for them. One first-page row is enough to know.
 * Errors (e.g. an older backend that rejects brand orgs here) read as "none". */
const useHasCampaignInvitations = (): boolean => {
  const { orgId, isBrand } = useSelector((state: RootState) => ({
    orgId: state?.SelectedOrganization?._id,
    isBrand: state?.SelectedOrganization?.type === 'brand',
  }));
  const [hasInvitations, setHasInvitations] = useState(false);

  useEffect(() => {
    setHasInvitations(false);
    if (!orgId || !isBrand) return undefined;
    let cancelled = false;
    const variables = {
      input: { listViewParams: { pagination: { limit: 1 } } },
    };
    // The sidebar and the Campaigns page both ask; one request serves both.
    sharedQuery(
      sharedQueryKey('OrganizationCampaignAuthTaskList', variables),
      () =>
        client().query({
          query: organizationCampaignAuthTaskList,
          // Any error means "no invitations"; a toast would be noise.
          context: { suppressGlobalErrorToast: true },
          variables,
          fetchPolicy: 'no-cache',
        }),
    )
      .then(({ data }) => {
        if (!cancelled)
          setHasInvitations(
            (data?.organizationCampaignAuthTaskList?.items?.length ?? 0) > 0,
          );
      })
      .catch(() => {
        // Older backend (creator-only endpoint) or transient failure: no tab.
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, isBrand]);

  return hasInvitations;
};

export default useHasCampaignInvitations;
